/**
 * The guide's localStorage preference. PCT docks the "?" in the top bar (the floating button is
 * only a fallback when the header is missing), so there is no floating on/off preference — only
 * language.
 */
import { HELP_BASE_LANGUAGE, HELP_LANGUAGES, type HelpLanguage } from './types';

const STORAGE_PREFIX = 'pct.help';

/**
 * Which language the guide — and the "Ask" panel, if you build it — answers in.
 *
 * Same reasoning as the floating button — one component's own chrome, kept in localStorage so a
 * user does not re-pick their language on every visit. Validated on read against the language
 * list, so adding a language needs no change here and a stale value cannot leak a language the
 * Ask API would reject (if you build it).
 */
export const HELP_LANGUAGE_KEY = `${STORAGE_PREFIX}.language`;

/**
 * PCT: with nothing stored the guide follows the app's own language (`fallback`, from i18next),
 * so a Lithuanian user opens a Lithuanian guide without picking anything.
 */
export const readLanguagePref = (fallback: HelpLanguage = HELP_BASE_LANGUAGE): HelpLanguage => {
  try {
    const stored = window.localStorage.getItem(HELP_LANGUAGE_KEY);
    const match = HELP_LANGUAGES.find((code) => code === stored);
    return match ?? fallback;
  } catch {
    return fallback;
  }
};

export const writeLanguagePref = (language: HelpLanguage): void => {
  try {
    window.localStorage.setItem(HELP_LANGUAGE_KEY, language);
  } catch {
    // Best-effort: the choice then lasts for this session only.
  }
};
